<template>
  <div class="cd-card">
    <div class="cd-card__head">
      <div>
        <div class="cd-card__title">{{ $t("customerDistribution") }}</div>
        <div class="cd-card__sub">{{ $t("customerCategoryShareDescription") }}</div>
      </div>
    </div>
    <div v-if="canSee" class="cd-card__body">
      <template v-if="hasData">
        <div class="cd-card__chart">
          <apexchart
            :key="chartKey"
            type="donut"
            width="132"
            height="132"
            :series="series"
            :options="chartOptions"
          />
        </div>
        <div class="cd-card__legend">
          <div
            v-for="(item, index) in rows"
            :key="item.name"
            class="cd-card__legend-row"
          >
            <span class="cd-card__legend-label">
              <span
                class="cd-card__dot"
                :style="{ background: colors[index % colors.length] }"
              />
              {{ item.name }}
            </span>
            <span class="cd-card__legend-value">{{ item.percent }}٪</span>
          </div>
        </div>
      </template>
      <div v-else class="cd-card__empty">
        <v-progress-circular
          v-if="loading"
          :width="3"
          color="blue"
          indeterminate
          size="22"
        />
        <home-empty-state v-else />
      </div>
    </div>
    <div v-else class="cd-card__empty">
      {{ $t("you don't have access to this information") }}
    </div>
  </div>
</template>

<script>
import HomeEmptyState from "./HomeEmptyState.vue";

export default {
  name: "HomeCustomerDistribution",
  components: { HomeEmptyState },
  props: {
    dateType: {
      type: String,
      default: "7days",
    },
  },
  data() {
    return {
      items: [],
      loading: false,
      colors: ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444"],
    };
  },
  computed: {
    canSee() {
      return (
        this.$store.getters.getpermission &&
        this.$store.getters.getpermission.includes("overview")
      );
    },
    hasData() {
      return this.series.some((val) => val > 0);
    },
    chartKey() {
      return this.dateType + "-" + this.items.length;
    },
    totalValue() {
      return this.items.reduce((sum, item) => sum + item.value, 0);
    },
    series() {
      return this.items.map((item) => item.value);
    },
    rows() {
      if (!this.totalValue) return [];
      return this.items.map((item) => ({
        ...item,
        percent: +((item.value / this.totalValue) * 100).toFixed(1),
      }));
    },
    chartOptions() {
      const self = this;
      return {
        chart: {
          type: "donut",
          fontFamily: "IRANSans",
          toolbar: { show: false },
          animations: { enabled: false },
          sparkline: { enabled: true },
        },
        labels: this.items.map((item) => item.name),
        colors: this.colors,
        legend: { show: false },
        stroke: {
          width: 2,
          colors: ["#fff"],
        },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            expandOnClick: false,
            donut: { size: "68%" },
          },
        },
        tooltip: {
          y: {
            formatter(value) {
              return self.$numberFormat(value);
            },
          },
        },
      };
    },
  },
  watch: {
    dateType() {
      this.fetchList();
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    fetchList() {
      this.loading = true;
      this.$axios
        .get(this.$endpoint + "dashboard/charts/users-by-category", {
          params: {
            dateType: this.dateType,
          },
        })
        .then((response) => {
          const res = (response.data && response.data.data) || [];
          this.items = res
            .map((item) => ({
              name: item.category_name,
              value: Math.abs(Number(item.users_count || 0)),
            }))
            .filter((item) => item.value > 0)
            .slice(-5);
        })
        .catch(() => {
          this.items = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.cd-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  padding: 12px 10px;
  height: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.cd-card__head {
  flex-shrink: 0;
  margin-bottom: 8px;
}
.cd-card__title {
  font-size: 13px;
  font-weight: 700;
  color: #111;
}
.cd-card__sub {
  margin-top: 4px;
  font-size: 11px;
  color: #6b7280;
}
.cd-card__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}
.cd-card__chart {
  flex: 0 0 132px;
  width: 132px;
  height: 132px;
}
.cd-card__legend {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cd-card__legend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.cd-card__legend-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  font-size: 12px;
  color: #111;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cd-card__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.cd-card__legend-value {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #111;
}
.cd-card__empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
}
</style>
